import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Check, Upload, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  apiAdminproductsPost,
  apiMediasPost,
  getApiShopsShopIdproductsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import {
  ProductMetadataProductWriteModelMetaKey,
  type ProductProductWriteModel
} from '@r/api/generated/client.schemas.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@ui/form.tsx';
import { Input } from '@ui/input.tsx';
import { Textarea } from '@ui/textarea.tsx';
import { Button } from '@ui/button.tsx';
import {
  buildCustomShopProductFormSchema,
  type CustomShopProductFormValues
} from './custom-shop-product-form-schema.ts';
import { CustomShopProductBrandSelect } from './custom-shop-product-brand-select.tsx';
import { getProductBrandIri } from './custom-shop-brands-api.ts';
import { CustomShopProductCategorySelect } from './custom-shop-product-category-select.tsx';
import { getProductCategoryIri } from './custom-shop-categories-api.ts';

interface CreateCustomShopProductFormProps {
  shopId: number;
  onCancel: () => void;
  onSuccess: () => void;
}

/** Crée un produit rattaché au catalogue d'une boutique custom. */
export function CreateCustomShopProductForm({
  shopId,
  onCancel,
  onSuccess
}: CreateCustomShopProductFormProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const formSchema = useMemo(() => buildCustomShopProductFormSchema(t), [t]);

  const form = useForm<CustomShopProductFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      description: '',
      price: undefined,
      tva: 0,
      shippingPrice: 0,
      categoryId: null,
      brandId: null,
      code: '',
      codeDetail: '',
      howtoUrl: '',
      images: [],
      designationCom: '',
      ean: '',
      stock: null,
      lowStockAlert: null
    },
    mode: 'onChange'
  });
  const stock = form.watch('stock');

  /** Téléverse une image produit et retourne son IRI pour le payload API. */
  const uploadImage = async (file: File): Promise<string> => {
    const media = await apiMediasPost({ file, type: 'layout' });
    if (!media.iri) throw new Error('Missing media IRI');
    return media.iri;
  };

  /** Assemble le payload produit, téléverse les images puis crée l'article. */
  const onSubmit = async (values: CustomShopProductFormValues) => {
    setIsSubmitting(true);

    try {
      // L'API produit attend les IRI des médias, les images sont donc téléversées en amont.
      const images = await Promise.all((values.images ?? []).map(uploadImage));
      const metadatas: ProductProductWriteModel['metadatas'] = [];

      // Les codes métier sont exposés par le formulaire mais stockés comme métadonnées produit.
      if (values.code) {
        metadatas.push({
          metaKey: ProductMetadataProductWriteModelMetaKey.code,
          metaValue: values.code
        });
      }

      if (values.codeDetail) {
        metadatas.push({
          metaKey: ProductMetadataProductWriteModelMetaKey.codeDetail,
          metaValue: values.codeDetail
        });
      }

      if (values.howtoUrl) {
        metadatas.push({
          metaKey: ProductMetadataProductWriteModelMetaKey.howtoUrl,
          metaValue: values.howtoUrl
        });
      }

      const payload: ProductProductWriteModel = {
        name: values.name.trim(),
        description: values.description || null,
        price: values.price ?? null,
        dynamicPrice: false,
        metadatas,
        shop: `/shops/${shopId}`,
        images,
        designationCom: values.designationCom || null,
        ean: values.ean || null,
        tva: values.tva,
        shippingPrice: values.shippingPrice,
        isDemat: metadatas.some(
          (metadata) => metadata.metaKey === ProductMetadataProductWriteModelMetaKey.code
        ),
        stock: values.stock,
        lowStockAlert: values.stock == null ? null : values.lowStockAlert,
        ...(values.categoryId != null
          ? { category: getProductCategoryIri(values.categoryId) }
          : {}),
        ...(values.brandId != null ? { brand: getProductBrandIri(values.brandId) } : {})
      };

      await apiAdminproductsPost(payload);
      await queryClient.invalidateQueries({
        queryKey: getApiShopsShopIdproductsGetCollectionQueryKey(String(shopId))
      });

      toast.success(t('admin.shops.products.create.success'));
      form.reset();
      onSuccess();
    } catch (error) {
      showOrvalErrorToast(error, t('admin.shops.products.create.error'));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form {...form} schema={formSchema}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="name"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.name.label')}</FormLabel>
                <FormControl>
                  <Input
                    placeholder={t('admin.shops.products.form.name.placeholder')}
                    maxLength={150}
                    error={!!fieldState.error}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="designationCom"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.designation.com.label')}</FormLabel>
                <FormControl>
                  <Input maxLength={255} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('admin.shops.products.form.description.label')}</FormLabel>
              <FormControl>
                <Textarea
                  rows={3}
                  placeholder={t('admin.shops.products.form.description.placeholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="price"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.price.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder={t('admin.shops.products.form.price.placeholder')}
                    error={!!fieldState.error}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(
                        event.target.value === '' ? undefined : event.target.valueAsNumber
                      )
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="ean"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('shop.product.ean.label')}</FormLabel>
                <FormControl>
                  <Input maxLength={20} error={!!fieldState.error} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="tva"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.tva.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    step="0.01"
                    placeholder={t('admin.shops.products.form.tva.placeholder')}
                    error={!!fieldState.error}
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.valueAsNumber)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.shops.products.form.tva.hint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="shippingPrice"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.shipping.price.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder={t('admin.shops.products.form.shipping.price.placeholder')}
                    error={!!fieldState.error}
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.valueAsNumber)}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.shops.products.form.shipping.price.hint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="categoryId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.category.label')}</FormLabel>
                <FormControl>
                  <CustomShopProductCategorySelect
                    shopId={shopId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="brandId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.brand.label')}</FormLabel>
                <FormControl>
                  <CustomShopProductBrandSelect
                    shopId={shopId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="stock"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.stock.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    placeholder={t('admin.shops.products.form.stock.placeholder')}
                    error={!!fieldState.error}
                    value={field.value ?? ''}
                    onChange={(event) => {
                      const value = event.target.value;
                      field.onChange(value === '' ? null : event.target.valueAsNumber);
                      if (value === '') form.setValue('lowStockAlert', null);
                    }}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  {t('admin.shops.products.form.stock.hint')}
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lowStockAlert"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.low.stock.alert.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min={0}
                    step={1}
                    disabled={stock == null}
                    placeholder={t('admin.shops.products.form.low.stock.alert.placeholder')}
                    error={!!fieldState.error}
                    value={field.value ?? ''}
                    onChange={(event) =>
                      field.onChange(event.target.value === '' ? null : event.target.valueAsNumber)
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <FormField
            control={form.control}
            name="code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.code.label')}</FormLabel>
                <FormControl>
                  <Input placeholder={t('admin.shops.products.form.code.placeholder')} {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="codeDetail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.code.detail.label')}</FormLabel>
                <FormControl>
                  <Textarea
                    rows={3}
                    placeholder={t('admin.shops.products.form.code.detail.placeholder')}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="howtoUrl"
            render={({ field, fieldState }) => (
              <FormItem>
                <FormLabel>{t('admin.shops.products.form.howto.url.label')}</FormLabel>
                <FormControl>
                  <Input
                    type="url"
                    placeholder={t('admin.shops.products.form.howto.url.placeholder')}
                    error={!!fieldState.error}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <FormField
          control={form.control}
          name="images"
          render={({ field }) => {
            const images = field.value ?? [];

            return (
              <FormItem>
                <FormLabel htmlFor="create-custom-shop-product-images">
                  {t('admin.shops.products.form.images.label')}
                </FormLabel>
                <FormControl>
                  <label className="flex min-h-28 cursor-pointer items-center justify-center gap-3 rounded-md border border-dashed bg-muted/20 p-4 text-center">
                    <Upload className="size-5 shrink-0" />
                    <span className="break-all text-sm">
                      {images.length
                        ? t('admin.shops.products.form.images.selected', {
                            count: images.length
                          })
                        : t('admin.shops.products.form.images.upload')}
                    </span>
                    <input
                      id="create-custom-shop-product-images"
                      className="sr-only"
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={(event) => field.onChange(Array.from(event.target.files ?? []))}
                    />
                  </label>
                </FormControl>
                {images.length > 0 && (
                  <ul className="space-y-1 text-xs text-muted-foreground">
                    {images.map((file) => (
                      <li key={`${file.name}-${file.lastModified}`}>{file.name}</li>
                    ))}
                  </ul>
                )}
                <FormMessage />
              </FormItem>
            );
          }}
        />

        <div className="flex gap-4 pt-4">
          <Button
            type="button"
            variant="outline"
            rounded="full"
            className="flex-1"
            disabled={isSubmitting}
            onClick={onCancel}
          >
            <X className="size-4" />
            {t('label.cancel')}
          </Button>
          <Button type="submit" rounded="full" className="flex-1" loading={isSubmitting}>
            {t('admin.shops.products.form.submit.create')}
            <Check className="size-4" />
          </Button>
        </div>
      </form>
    </Form>
  );
}
